import { supabase } from "../lib/supabase";
import { Container } from "../types/booking";

/**
 * Fetch all active containers for filtering
 */
export const fetchContainers = async () => {
  try {
    const { data, error } = await supabase
      .from("containers")
      .select(`
        *,
        container_origin_port:booking_origin_port(*),
        container_destination_port:booking_destination_port(*)
      `)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { 
      data: data as Container[], 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching containers:", error);
    return { 
      data: [] as Container[], 
      error 
    };
  }
};

/**
 * Fetch a single container by ID with full details
 */
export const fetchContainerById = async (id: number) => {
  try {
    const { data, error } = await supabase
      .from("containers")
      .select(`
        *,
        container_origin_port:booking_origin_port(*),
        container_destination_port:booking_destination_port(*)
      `)
      .eq("id", id)
      .single();

    if (error) throw error;

    return { 
      data: data as Container, 
      error: null 
    };
  } catch (error) {
    console.error(`Error fetching container with ID ${id}:`, error);
    return { 
      data: null, 
      error 
    };
  }
}; 

export interface ContainerEmailData {
  companyName: string;
  email: string;
  bookingId: number;
}

export const getContainerEmailsDetailed = async (containerReference: string): Promise<ContainerEmailData[]> => {
  try {
    // Get bookings with company information
    const { data: bookingsData, error: bookingsError } = await supabase
      .from('bookings')
      .select(`
        id,
        company:company_id (
          id,
          name,
          user_companies (
            user_id
          )
        ),
        containers!inner (
          reference
        )
      `)
      .eq('containers.reference', containerReference);

    if (bookingsError) {
      console.error('Error fetching bookings:', bookingsError);
      return [];
    }

    if (!bookingsData || bookingsData.length === 0) {
      console.log('No bookings found for container:', containerReference);
      return [];
    }

    // Get auth token for admin function access
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.error('No auth session found');
      return [];
    }

    // Process each booking to get company emails
    const emailDataList: ContainerEmailData[] = [];
    const processedCompanyEmails = new Set<string>(); // To avoid company-email duplicates
    const processedCompanies = new Set<number>(); // To avoid processing same company multiple times
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const booking of bookingsData as any[]) {
      const company = booking.company;
      if (!company?.user_companies || processedCompanies.has(company.id)) continue;

      processedCompanies.add(company.id);
      const companyName = company.name || `Company ${company.id}`;
      
      // Get emails for all users in this company
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const uc of company.user_companies as any[]) {
        if (!uc.user_id) continue;

        try {
          const { data: userData, error: userError } = await supabase.functions.invoke(
            'admin-users/get-user',
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
              body: {
                userId: uc.user_id,
              },
            }
          );

          if (!userError && userData?.user?.email && 
              userData.user.email !== 'info@efreightforwarding.com') {
            
            const companyEmailKey = `${company.id}-${userData.user.email}`;
            if (!processedCompanyEmails.has(companyEmailKey)) {
              processedCompanyEmails.add(companyEmailKey);
              emailDataList.push({
                companyName,
                email: userData.user.email,
                bookingId: booking.id,
              });
            }
          }
        } catch (userFetchError) {
          console.warn('Error fetching user:', uc.user_id, userFetchError);
        }
      }
    }

    // Sort by company name, then by email
    const sortedEmailData = emailDataList.sort((a, b) => {
      if (a.companyName !== b.companyName) {
        return a.companyName.localeCompare(b.companyName);
      }
      return a.email.localeCompare(b.email);
    });
    
    console.log(`Found ${sortedEmailData.length} emails for container ${containerReference}:`, sortedEmailData);
    
    return sortedEmailData;
  } catch (error) {
    console.error('Error in getContainerEmails:', error);
    return [];
  }
};

// Simple function that returns just the emails for backward compatibility
export const getContainerEmails = async (containerReference: string): Promise<string[]> => {
  const detailedData = await getContainerEmailsDetailed(containerReference);
  return detailedData.map(item => item.email);
}; 